import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Lock, Mail, User, ArrowRight } from "lucide-react";

const RegisterPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Registrazione completata con successo");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la registrazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left side - Decorative */}
      <div className="hidden lg:flex w-1/2 bg-[#0A0A0A] items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-tl from-[#D4AF37]/5 to-transparent" />
        <div className="relative z-10 p-16 text-center">
          <div className="w-24 h-24 mx-auto mb-8 border border-[#D4AF37]/30 rounded-full flex items-center justify-center">
            <div className="w-16 h-16 border border-[#D4AF37]/50 rounded-full flex items-center justify-center">
              <div className="w-8 h-8 bg-[#D4AF37]/20 rounded-full" />
            </div>
          </div>
          <blockquote className="text-2xl text-[#EDEDED] font-serif italic mb-4">
            "Inizia oggi il tuo percorso verso l'eccellenza"
          </blockquote>
          <p className="text-[#52525B] uppercase tracking-widest text-xs">
            Unisciti ai nostri clienti
          </p>
        </div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md animate-fade-in">
          <div className="mb-12">
            <h1 className="text-3xl lg:text-4xl font-semibold text-[#EDEDED] mb-3">
              Crea Account
            </h1>
            <p className="text-[#A1A1AA] text-sm">
              Inizia a gestire i tuoi documenti in modo professionale
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#A1A1AA] uppercase tracking-wider text-xs">
                Nome Completo
              </Label>
              <div className="relative">
                <User className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-transparent border-0 border-b border-neutral-700 rounded-none pl-8 focus:border-[#D4AF37] focus:ring-0 text-[#EDEDED] placeholder:text-neutral-600"
                  placeholder="Mario Rossi"
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#A1A1AA] uppercase tracking-wider text-xs">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-transparent border-0 border-b border-neutral-700 rounded-none pl-8 focus:border-[#D4AF37] focus:ring-0 text-[#EDEDED] placeholder:text-neutral-600"
                  placeholder="nome@esempio.it"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#A1A1AA] uppercase tracking-wider text-xs">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-transparent border-0 border-b border-neutral-700 rounded-none pl-8 focus:border-[#D4AF37] focus:ring-0 text-[#EDEDED] placeholder:text-neutral-600"
                  placeholder="Minimo 6 caratteri"
                  required
                  data-testid="register-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#D4AF37] text-black hover:bg-[#B5952F] font-medium rounded-sm transition-all duration-300 uppercase tracking-widest text-xs py-6 mt-8"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Registrati
                  <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-[#52525B] text-sm">
            Hai già un account?{" "}
            <Link
              to="/login"
              className="text-[#D4AF37] hover:text-[#B5952F] transition-colors"
              data-testid="login-link"
            >
              Accedi
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
